import React from 'react';
import { getModeForUsageLocation } from 'typescript';
import ImageWithFallback from '../img-with-fallback/image-with-fallback';

import config from '../../../config/config';

export interface MediaProps {
  src: string;
  googleMedia?: { id: string, mimeType: string, width: number, height: number },
  fallback?: React.ReactNode
}

// Helper function to determine the media type
export const getMediaType = (src: string) => {

  const imageTypes = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic'];
  const videoTypes = ['.mp4', '.webm', '.avi', '.mov'];
  const audioTypes = ['.mp3', '.ogg', '.wav', '.flac'];
  const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube|youtu|vimeo)\.(?:com|tv)\/(?:.*?)(v=|\/)([a-zA-Z0-9_-]+)/;
  const vimeoRegex = /vimeo\.com\/(\d+)/;
  const dailymotionRegex = /dailymotion\.com\/video\/([a-zA-Z0-9_-]+)/;
  const twitchRegex = /twitch\.tv\/([a-zA-Z0-9_-]+)/;
  const facebookRegex = /facebook\.com\/.*\/videos\/(\d+)/;
  const instagramRegex = /instagram\.com\/.*\/p\/([a-zA-Z0-9_-]+)/;
  const tiktokRegex = /tiktok\.com\/.*\/video\/(\d+)/;
  const peerTubeRegex = /peertube\.xyz\/videos\/(\d+)/; // Adjust domain as needed
  const rumbleRegex = /rumble\.com\/v\/([a-zA-Z0-9_-]+)/;
  const youkuRegex = /youku\.com\/.*id_([a-zA-Z0-9_-]+)/;
  const streamRegex = /stream\.microsoft\.com\/.*\/videos\/([a-zA-Z0-9_-]+)/;
  const googleDriveRegex = /^https:\/\/drive\.google\.com\/(file\/d\/|open\?id=)[A-Za-z0-9_-]+(?:\/view\?usp=sharing)?$/;


  if (imageTypes.some(ext => src.endsWith(ext))) {
    return 'image';
  } else if (videoTypes.some(ext => src.endsWith(ext))) {
    return 'video';
  } else if (audioTypes.some(ext => src.endsWith(ext))) {
    return 'audio';
  } else if (youtubeRegex.test(src)) {
    return 'youtube';
  } else if (vimeoRegex.test(src)) {
    return 'vimeo';
  } else if (dailymotionRegex.test(src)) {
    return 'dailymotion';
  } else if (twitchRegex.test(src)) {
    return 'twitch';
  } else if (facebookRegex.test(src)) {
    return 'facebook';
  } else if (instagramRegex.test(src)) {
    return 'instagram';
  } else if (tiktokRegex.test(src)) {
    return 'tiktok';
  } else if (peerTubeRegex.test(src)) {
    return 'peertube';
  } else if (rumbleRegex.test(src)) {
    return 'rumble';
  } else if (youkuRegex.test(src)) {
    return 'youku';
  } else if (streamRegex.test(src)) {
    return 'stream';
  } else if (googleDriveRegex.test(src)) {
    return 'googleDrive'
  }
  return 'unknown';
};

  // Function to extract video IDs from URLs for certain platforms
export const extractVideoId = (src: string, type: string) => {



  const youtubeMatch = src.match(/(?:v=|\/)([a-zA-Z0-9_-]+)/);
  const vimeoMatch = src.match(/vimeo\.com\/(\d+)/);
  const dailymotionMatch = src.match(/dailymotion\.com\/video\/([a-zA-Z0-9_-]+)/);
  const twitchMatch = src.match(/twitch\.tv\/([a-zA-Z0-9_-]+)/);
  const facebookMatch = src.match(/facebook\.com\/.*\/videos\/(\d+)/);
  const instagramMatch = src.match(/instagram\.com\/.*\/p\/([a-zA-Z0-9_-]+)/);
  const tiktokMatch = src.match(/tiktok\.com\/.*\/video\/(\d+)/);
  const peerTubeMatch = src.match(/peertube\.xyz\/videos\/(\d+)/);
  const rumbleMatch = src.match(/rumble\.com\/v\/([a-zA-Z0-9_-]+)/);
  const youkuMatch = src.match(/youku\.com\/.*id_([a-zA-Z0-9_-]+)/);
  const streamMatch = src.match(/stream\.microsoft\.com\/.*\/videos\/([a-zA-Z0-9_-]+)/);
  const googleDriveMatch = src.match(/(?:https:\/\/drive\.google\.com\/(?:file\/d\/|open\?id=))([^\/?&]+)/);

  switch (type) {
    case 'youtube':
      return youtubeMatch ? youtubeMatch[1] : '';
    case 'vimeo':
      return vimeoMatch ? vimeoMatch[1] : '';
    case 'dailymotion':
      return dailymotionMatch ? dailymotionMatch[1] : '';
    case 'twitch':
      return twitchMatch ? twitchMatch[1] : '';
    case 'facebook':
      return facebookMatch ? facebookMatch[1] : '';
    case 'instagram':
      return instagramMatch ? instagramMatch[1] : '';
    case 'tiktok':
      return tiktokMatch ? tiktokMatch[1] : '';
    case 'peertube':
      return peerTubeMatch ? peerTubeMatch[1] : '';
    case 'rumble':
      return rumbleMatch ? rumbleMatch[1] : '';
    case 'youku':
      return youkuMatch ? youkuMatch[1] : '';
    case 'stream':
      return streamMatch ? streamMatch[1] : '';
    case 'googleDrive':
      return googleDriveMatch ? googleDriveMatch[1] : ''
    default:
      return '';
  }
};
const Media: React.FC<MediaProps> = ({ src, googleMedia, fallback }) => {

  // console.log('init src: ', src);
  // last minute hack to keep dev and prod env working
  if (config.ENVIRONMENT === "PRODUCTION") {
    if (src.startsWith(config.ASSETS.DEVELOPMENT)) {
      src = config.ASSETS.PRODUCTION + src.slice(config.ASSETS.DEVELOPMENT.length);
    }
    if (src.startsWith("http://" + config.ASSETS.DEVELOPMENT)) {
      src = config.ASSETS.PRODUCTION + src.slice(("http://" + config.ASSETS.DEVELOPMENT).length);
    }
    if (src.startsWith("https://" + config.ASSETS.DEVELOPMENT)) {
      src = config.ASSETS.PRODUCTION + src.slice(("https://" + config.ASSETS.DEVELOPMENT).length);
    }
  }

  if (config.ENVIRONMENT === "DEVELOPMENT") {
    if (!src.startsWith(config.ASSETS.DEVELOPMENT)) {
      src = config.ASSETS.DEVELOPMENT + src.slice(13);
    }
  }

  // console.log('altered src: ', src );

  const mediaType = getMediaType(src);

  if (googleMedia && googleMedia.mimeType.startsWith('image')) {
    return (
      <ImageWithFallback 
        src={`https://drive.google.com/thumbnail?id=${googleMedia.id}&sz=w${googleMedia.width}`}
        alt={'Image Missing'} 
        fallbackContent={fallback} 
      />
    )
  }

  return (
    <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
      {mediaType === 'image' && (
        fallback ?
          // <div>
          //   <div className="text-black">{src}</div>
           <ImageWithFallback src={src} alt={'Image Missing'} fallbackContent={fallback}/>
          // </div>

        :
          <img src={src} alt="Media not found" className="object-cover w-full h-full" />
      )}
      {mediaType === 'video' && (
        <video className="object-cover w-full h-full" autoPlay loop muted playsInline webkit-playsInline webkit-playsinline controlsList="nodownload">
          <source src={src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
      {mediaType === 'audio' && (
        <audio className="w-full" controls>
          <source src={src} type="audio/mp3" />
          Your browser does not support the audio element.
        </audio>
      )}
      {mediaType === 'youtube' && (
        <iframe
          title='YouTube Video'
          className="w-full h-full absolute top-0 left-0"
          src={src}//{`https://www.youtube.com/embed/${extractVideoId(src, 'youtube')}`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
      {mediaType === 'vimeo' && (
        <iframe
          title='Vimeo Video'
          className="w-full h-full absolute top-0 left-0"
          src={`https://player.vimeo.com/video/${extractVideoId(src, 'vimeo')}`}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
        />
      )}
      {mediaType === 'dailymotion' && (
        <iframe
          className="w-full h-full absolute top-0 left-0"
          src={`https://www.dailymotion.com/embed/video/${extractVideoId(src, 'dailymotion')}`}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}
      {mediaType === 'twitch' && (
        <iframe
          className="w-full h-full absolute top-0 left-0"
          src={`https://player.twitch.tv/?channel=${extractVideoId(src, 'twitch')}`}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}
      {mediaType === 'facebook' && (
        <iframe
          className="w-full h-full absolute top-0 left-0"
          src={`https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/facebook/videos/${extractVideoId(src, 'facebook')}`}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}
      {mediaType === 'instagram' && (
        <iframe
          className="w-full h-full absolute top-0 left-0"
          src={`https://www.instagram.com/p/${extractVideoId(src, 'instagram')}/embed`}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}
      {mediaType === 'tiktok' && (
        <iframe
          className="w-full h-full absolute top-0 left-0"
          src={`https://www.tiktok.com/embed/v2/${extractVideoId(src, 'tiktok')}`}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}
      {mediaType === 'peertube' && (
        <iframe
          className="w-full h-full absolute top-0 left-0"
          src={`https://peertube.xyz/videos/embed/${extractVideoId(src, 'peertube')}`} // Adjust domain as needed
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}
      {mediaType === 'rumble' && (
        <iframe
          className="w-full h-full absolute top-0 left-0"
          src={`https://rumble.com/embed/${extractVideoId(src, 'rumble')}`}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}
      {mediaType === 'youku' && (
        <iframe
          className="w-full h-full absolute top-0 left-0"
          src={`https://player.youku.com/embed/${extractVideoId(src, 'youku')}`}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}
      {mediaType === 'stream' && (
        <iframe
          className="w-full h-full absolute top-0 left-0"
          src={`https://stream.microsoft.com/video/${extractVideoId(src, 'stream')}`}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
        />
      )}
      {mediaType === 'googleDrive' && (
        <div className="relative w-full pb-[56.25%]">
          <iframe
            // onLoad={(e) => {
            //   const iFrameDoc = (e.target as HTMLIFrameElement).contentWindow?.document;
            //   if (iFrameDoc) {
            //     const contentWidth = iFrameDoc.body.scrollWidth;
            //     const contentHeight = iFrameDoc.body.scrollHeight;
          
            //     if (contentWidth && contentHeight) {
            //       const ratio = contentHeight / contentWidth;
            //       console.log(ratio);
            //     }
            //   }
            // }}
            className="absolute top-0 left-0 w-full h-full border-0"
            src={`https://drive.google.com/file/d/${extractVideoId(src, 'googleDrive')}/preview`}
            frameBorder="0"
            allow="autoplay; fullscreen"
            allowFullScreen
          />


        </div>

      )}
      {mediaType === 'unknown' && (
        fallback ? 
          fallback
        :
          <div className="text-center text-white bg-black p-4">
            Unsupported media type
          </div>
      )}
    </div>
  );
};

export default Media;
